// apps/web/src/components/v2/SegmentedControl.tsx
import type { ReactElement } from "react";
// The `seg` variant is the DS segmented control (ds.css .ds-seg).
import "./ds/ds.css";

export interface SegmentedControlOption<T extends string> {
  value: T;
  label: string;
}

/**
 * SegmentedControl — the v2 pill toggle (PeriodTabs geometry, generalized).
 * Tokens only: PeriodTabs uses an arbitrary-hex background that is a ratchet
 * violation this file must not copy — bg-gray-100 is the token neighbour.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  variant = "pill",
}: {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (v: T) => void;
  ariaLabel: string;
  /** `seg` = the redesign mock's `.seg` (built-v12): hairline well, white
   *  active pill. Opt-in, so every existing caller renders as before. */
  variant?: "pill" | "seg";
}): ReactElement {
  const seg = variant === "seg";
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      // On a phone the bar scrolls sideways inside itself (ds.css .ds-seg-bar), by design.
      data-allow-scroll={seg ? "tabs" : undefined}
      className={
        seg
          ? "ds-seg ds-seg-bar"
          : "inline-flex gap-1 rounded-xl bg-gray-100 p-1"
      }
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(opt.value)}
            className={
              seg
                ? undefined
                : `rounded-lg px-3 py-1.5 text-task font-semibold transition-colors ${
                    active
                      ? "bg-white text-brand-navy shadow-sm"
                      : "text-gray-600 hover:text-brand-navy"
                  }`
            }
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
